import React, {
  useCallback,
  useContext,
  useEffect,
  useMemo,
  useRef,
  useState,
} from "react";

import { GridRows } from "@visx/grid";
import { Group } from "@visx/group";
import { withParentSize } from "@visx/responsive";

import { isEqual } from "es-toolkit/compat";

import { colours } from "../../constants/colours";
import { compose, useColourScale, useXScale, useYScale } from "../helpers";

import { BottomAxis, SideAxis } from "../components/Axes";
import { Brush } from "../components/Brush";
import {
  ChartTooltipContext,
  ChartTooltipWrapper,
} from "../components/ChartTooltip";

import XYChartBars from "./XYChartBars";
import { XYChartGuide } from "./XYChartGuide";
import { XYChartLegend } from "./XYChartLegend";
import XYChartLines from "./XYChartLines";
import XYChartScatterPlots from "./XYChartScatterPlots";
import { XYChartThresholds } from "./XYChartThresholds";
import { XYChartTooltip } from "./XYChartTooltip";
import XYChartZeroLines from "./XYChartZeroLines";

import { Data } from "../types";
import type {
  DataPoint,
  TimeSeriesChartProps,
  TooltipItem,
  XYChartProps,
} from "./types";
import {
  getChartSpacing,
  getLegendItems,
  setsToBars,
  setsToLines,
} from "./util";

/* eslint-disable react/prop-types */

const XYChart = ({
  sets,
  parentWidth,
  height = 500,
  leftAxis,
  rightAxis,
  bottomAxis,
  numberOfTicks,
  from,
  to,
  interval = "1day",
  thresholds = [],
  isLegendVisible = true,
  isBrushVisible = true,
}: XYChartProps & TimeSeriesChartProps) => {
  const brushRef = useRef<any>(null);

  const lines = useMemo(() => setsToLines(sets), [sets]);
  const bars = useMemo(() => setsToBars(sets), [sets]);

  const hasBrush = !!bars?.length;

  const bottomAccessor = bottomAxis?.valueAccessor;

  const [displayFrom, setDisplayFrom] = useState(() => {
    if (from) {
      return sets.reduce((currentEarliestDate: Date, { data }) => {
        const earliestDateInData = data.reduce(
          (currentEarliestDataDate: Date, dataPoint: DataPoint) => {
            const comparisonDate = new Date(bottomAccessor(dataPoint));

            return comparisonDate < currentEarliestDataDate
              ? comparisonDate
              : currentEarliestDataDate;
          },
          new Date(bottomAccessor(data[0]))
        );

        return earliestDateInData < currentEarliestDate
          ? earliestDateInData
          : currentEarliestDate;
      }, new Date(from));
    } else return;
  });

  const [displayTo, setDisplayTo] = useState(() => {
    if (to) {
      return sets.reduce((currentLatestDate: Date, { data }) => {
        const latestDateInData = data.reduce(
          (currentLatestDataDate: Date, dataPoint: DataPoint) => {
            const comparisonDate = new Date(bottomAccessor(dataPoint));

            return comparisonDate > currentLatestDataDate
              ? comparisonDate
              : currentLatestDataDate;
          },
          new Date(bottomAccessor(data[0]))
        );

        return latestDateInData > currentLatestDate
          ? latestDateInData
          : currentLatestDate;
      }, new Date(to));
    } else return;
  });

  const legendItems = useMemo(
    () => getLegendItems(sets, [...thresholds]),
    [sets, thresholds]
  );

  const [filteredSets, setFilteredSets] = useState(sets);

  useEffect(() => {
    setFilteredSets(sets);
  }, [sets]);

  const {
    yMax,
    xMax,
    leftAxisWidth,
    rightAxisWidth,
    chartSeparation,
    bottomChartHeight,
    chartHeight,
    marginTop,
  } = useMemo(
    () => getChartSpacing(height, parentWidth, hasBrush),
    [height, parentWidth, hasBrush]
  );

  const leftSets = useMemo(
    () => filteredSets.filter(({ axis }) => axis === "left"),
    [filteredSets]
  );

  const leftAccessor = leftAxis?.valueAccessor;

  const rightAccessor = rightAxis?.valueAccessor;

  const filteredBars = useMemo(
    () => filteredSets.filter(({ variant }) => variant === "bar"),
    [filteredSets]
  );

  const leftScale = useYScale({
    yMax,
    data: leftSets.reduce(
      (total: number[], { data }) => [
        ...total,
        ...data.map((point: any) => leftAccessor?.(point)),
      ],
      // Make sure the scale includes 0
      leftSets.length ? [0] : []
    ),
    maxThreshold: thresholds.reduce(
      (currentMax, { axis, value }) =>
        axis === "left" && value > currentMax ? value : currentMax,
      0
    ),
    hasBars: !!filteredBars?.length,
  });

  const rightScale = useYScale({
    yMax,
    data: filteredSets.reduce(
      (total: number[], { data, axis }) =>
        axis === "right"
          ? [...total, ...data.map((point: any) => rightAccessor?.(point))]
          : total,
      // Make sure the scale includes 0
      filteredSets.length ? [0] : []
    ),
    maxThreshold: thresholds.reduce(
      (currentMax, { axis, value }) =>
        axis === "right" && value > currentMax ? value : currentMax,
      0
    ),
    hasBars: !!filteredBars?.length,
  });

  const { showTooltip, hideTooltip, containerBounds } =
    useContext(ChartTooltipContext);

  const [hoveredLegendItem, setHoveredLegendItem] = useState<
    number | undefined
  >(undefined);

  const [verticalGuidelinePosition, setVerticalGuidelinePosition] = useState<
    number | undefined
  >(undefined);

  const filteredLines = useMemo(
    () => filteredSets?.filter(({ variant }) => variant === "line") || [],
    [filteredSets]
  );

  const filteredScatterPlots = useMemo(
    () =>
      filteredSets?.filter(({ variant }) => variant === "scatter-plot") || [],
    [filteredSets]
  );

  const onBrushChange = (domain: any) => {
    if (!domain) {
      setFilteredSets(sets);
    } else if (from && to) {
      const { x0, x1 } = domain;

      setDisplayFrom(new Date(x0));
      setDisplayTo(new Date(x1));

      setFilteredSets(
        sets.map(({ data, ...rest }) => ({
          ...rest,
          data: data.filter((point: any) => {
            const date = bottomAccessor(point)
              ? new Date(bottomAccessor(point))
              : null;
            const x = date?.getTime();

            if (x) {
              return x > x0 && x < x1;
            }
            return false;
          }),
        }))
      );
    } else {
      setFilteredSets(
        sets.map(({ data, ...rest }) => ({
          ...rest,
          data: data.filter((point: any) =>
            domain.xValues.find((date: Date) =>
              isEqual(date, bottomAccessor(point))
            )
          ),
        }))
      );
    }
  };

  const {
    scale: bottomScale,
    quantizedScale: quantizedBottomScale,
    getPoint: getBottomPoint,
    getNearestPosition,
  } = useXScale({
    xMax,
    data: filteredSets,
    comparator: bottomAxis.compare,
    bottomAccessor,
    variant: bars?.length ? "bar" : "line",
    tickRenderer: bottomAxis.tickRenderer,
    from: displayFrom,
    to: displayTo,
    interval,
  });

  const getLeftPoint = useCallback(compose(leftScale, leftAccessor), [
    leftScale,
    leftAccessor,
  ]);

  const getRightPoint = useCallback(
    rightAccessor ? compose(rightScale, rightAccessor) : () => null,
    [rightScale, rightAccessor]
  );

  const coloursScale = useColourScale({ sets });
  const lineGradientsScale = useColourScale({ sets, variant: "area" });

  const onPointerMove = useCallback(
    (event: React.PointerEvent<HTMLDivElement>) => {
      if (showTooltip) {
        const x = ("clientX" in event ? event.clientX : 0) - containerBounds.x;

        const y = ("clientY" in event ? event.clientY : 0) - containerBounds.y;

        const bottomKey = quantizedBottomScale?.(x - leftAxisWidth);

        if (bottomKey && x > leftAxisWidth && y < yMax) {
          const left = getNearestPosition(x - leftAxisWidth);

          setVerticalGuidelinePosition(left);

          showTooltip({
            tooltipLeft: left + 70,
            tooltipTop: yMax / 3,
            component: (
              <XYChartTooltip
                date={bottomKey as string}
                items={sets.reduce(
                  (
                    total: TooltipItem[],
                    { data, variant, axis, label, itemSpecificTooltipLabel }
                  ) => {
                    const dataPoints: Data = data?.filter(
                      (point: DataPoint) => {
                        const value = bottomAccessor(point);

                        const formattedValue = bottomAxis.tickRenderer
                          ? bottomAxis.tickRenderer(value)
                          : value;

                        return formattedValue === bottomKey;
                      }
                    );

                    if (dataPoints) {
                      const axisObject = axis === "left" ? leftAxis : rightAxis;

                      const colour = coloursScale(label);

                      const newEntires = dataPoints.map((dataPoint) => {
                        const value = axisObject?.valueRenderer
                          ? axisObject?.valueRenderer(dataPoint)
                          : axisObject?.valueAccessor(dataPoint);

                        const labelForTooltip = itemSpecificTooltipLabel
                          ? itemSpecificTooltipLabel(dataPoint)
                          : label;

                        return {
                          value,
                          variant,
                          colour,
                          label: labelForTooltip,
                        };
                      });
                      return [...total, ...newEntires];
                    }

                    return total;
                  },
                  []
                )}
              />
            ),
          });
        } else {
          setVerticalGuidelinePosition(undefined);

          if (hideTooltip) {
            hideTooltip();
          }
        }
      }
    },
    [showTooltip, hideTooltip, quantizedBottomScale, bottomScale]
  );

  const onMouseLeave = useCallback(() => {
    setVerticalGuidelinePosition(undefined);

    hideTooltip?.();
  }, [hideTooltip]);

  const onLegendItemPointerMove = (
    _event: React.PointerEvent<HTMLDivElement>,
    point: DataPoint
  ) => {
    if (point && point.variant !== "threshold") {
      const { label } = point;

      setHoveredLegendItem(label);
    } else {
      setHoveredLegendItem(undefined);
    }
  };

  const commonChartProps = {
    bottomAccessor,
    leftAccessor,
    rightAccessor,
    leftAxisWidth,
    rightAxisWidth,
    chartSeparation,
    bottomChartHeight,
    chartHeight,
    yMax,
    xMax,
    hoveredLegendItem,
    bottomAxis,
    bottomScale,
    getBottomPoint,
    quantizedBottomScale,
    leftScale,
    getLeftPoint,
    rightScale,
    getRightPoint,
    leftAxis,
    rightAxis,
    from: displayFrom,
    to: displayTo,
    interval,
    coloursScale,
  };

  return (
    <div
      onPointerMove={onPointerMove}
      onMouseLeave={onMouseLeave}
      style={{ marginTop }}
    >
      <svg
        width={parentWidth}
        height={height}
      >
        {leftAxis ? (
          <SideAxis
            axis={leftAxis}
            top={marginTop}
            left={leftAxisWidth}
            numberOfTicks={numberOfTicks}
            scale={leftScale}
          />
        ) : null}

        <XYChartZeroLines
          leftScale={leftScale}
          rightScale={rightScale}
          left={leftAxisWidth}
          width={xMax}
          marginTop={marginTop}
          yMax={yMax}
        />

        <Group
          left={leftAxisWidth}
          width={xMax}
          top={marginTop}
        >
          <GridRows
            scale={leftAxis ? leftScale : rightScale}
            width={xMax}
            height={yMax}
            stroke={colours.surface["100"]}
            strokeOpacity={0.25}
            numTicks={numberOfTicks}
          />

          {filteredBars ? (
            <XYChartBars
              data={filteredBars}
              hasLines={!!lines?.length}
              {...commonChartProps}
            />
          ) : null}

          {filteredLines?.length ? (
            <>
              <XYChartLines
                legendItems={legendItems}
                data={filteredLines}
                lineGradientsScale={lineGradientsScale}
                {...commonChartProps}
              />
            </>
          ) : null}

          {filteredScatterPlots?.length ? (
            <>
              <XYChartScatterPlots
                data={filteredScatterPlots}
                {...commonChartProps}
              />
            </>
          ) : null}

          {verticalGuidelinePosition ? (
            <XYChartGuide
              yMax={yMax}
              verticalGuidelinePosition={verticalGuidelinePosition}
            />
          ) : null}
        </Group>

        <XYChartThresholds
          thresholds={thresholds}
          leftScale={leftScale}
          rightScale={rightScale}
          bottomScale={bottomScale}
          leftAxisWidth={leftAxisWidth}
          xMax={xMax}
          yMax={yMax}
          parentWidth={parentWidth}
        />

        {rightAxis ? (
          <SideAxis
            axis={rightAxis}
            top={marginTop}
            left={xMax + rightAxisWidth}
            numberOfTicks={numberOfTicks}
            scale={rightScale}
            variant="right"
          />
        ) : null}

        <BottomAxis
          axis={bottomAxis}
          left={leftAxisWidth}
          top={yMax + marginTop}
          bottomScale={bottomScale}
        />

        {isBrushVisible && hasBrush ? (
          <Brush
            {...commonChartProps}
            top={chartHeight + chartSeparation + marginTop}
            left={leftAxisWidth}
            width={xMax}
            height={bottomChartHeight}
            sets={sets}
            onChange={onBrushChange}
            thresholds={thresholds}
            ref={brushRef}
          />
        ) : null}
      </svg>

      {isLegendVisible && legendItems ? (
        <XYChartLegend
          scale={coloursScale}
          onPointerMove={onLegendItemPointerMove}
          data={legendItems}
          left={leftAxisWidth}
        />
      ) : null}
    </div>
  );
};

const WrappedXYChart: ReturnType<typeof withParentSize> = withParentSize(
  (props) => (
    <ChartTooltipWrapper
      {...props}
      // @ts-expect-error: withParentSize type error, not ours
      component={XYChart}
    />
  )
);

export default WrappedXYChart;
